/**
 * The gardener's own picture: showing it, replacing it, removing it.
 *
 * Self-contained because it shares nothing with the rest of the profile page
 * but the user it belongs to — it has its own photo picker, its own uploading
 * state, and its own way of failing: in a toast, in the server's words when
 * it refused the file (not a photo, unreadable, too large), and in ours
 * otherwise. An iPhone photo (HEIC) is made a JPEG before it is sent; one
 * that cannot be is said as that.
 *
 * The picture is chosen with the same three ways as a plant's (PhotoSourcePicker:
 * camera, this device, Google Photos, useGooglePhotos / VITE_GOOGLE_CLIENT_ID),
 * in a screen of its own opened by the Change / Upload button.
 *
 * Used by: ProfileDetailsForm, at the top of the details screen.
 * Waiting on the server (B111): the Change / Upload button, the avatar's dim and the remove panel's Delete use the shared Button
 * loading / Spinner, so the waiting word is Button's own (Uploading, Deleting); the remove panel's foot is ActionRow (Keep it, Delete).
 *
 * @module ProfilePhotoCard
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Upload } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import Spinner from '@/shared/components/ui/Spinner';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { preparePhoto } from '@/shared/lib/preparePhoto';
import { toast } from '@/shared/components/ui/use-toast';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useAuth } from '@/shared/hooks/useAuth';
import { uploadProfilePhoto, deleteProfilePhoto } from '@/shared/api/users';
import { debugLog } from '@/config/config';
import { UserAvatar } from '@/shared/components/layout/UserAvatar';
import { useGrowLimits } from '@/shared/lib/growSettings';
import { errorMessage } from '@/shared/lib/errorMessage';
import { PhotoDialogShell } from '@/features/plant-list/components/dialogs/photo/PhotoDialogShell';
import { PhotoSourcePicker } from '@/features/plant-list/components/dialogs/photo/PhotoSourcePicker';
import { useCamera } from '@/features/plant-list/components/dialogs/photo/useCamera';
import { useGooglePhotos } from '@/features/plant-list/components/dialogs/photo/useGooglePhotos';

/**
 * The picture, beside the name it belongs to, at the top of "Name, email and
 * address". A new picture is kept the moment it is chosen, as a tap is;
 * removing one asks first, because it was the one thing on the profile that
 * vanished at a touch.
 */
export const ProfilePhotoCard: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  /** Where photos are served from: the server's (limits.SPACES_BASE_URL). */
  const spaces = useGrowLimits().SPACES_BASE_URL;
  const { mysqlUser, updateMysqlUser, currentUser } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  /** True while the photo source screen (camera, device, Google Photos) is open. */
  const [choosingPhoto, setChoosingPhoto] = useState(false);
  const camera = useCamera();
  const google = useGooglePhotos(choosingPhoto, { kind: 'profile' });
  // The camera is released whenever the source screen closes.
  const { stop: stopCamera } = camera;
  useEffect(() => {
    if (!choosingPhoto) stopCamera();
  }, [choosingPhoto, stopCamera]);

  /*
   * The picture on screen: only our own upload, as everyone else sees it. The
   * sign-in provider's photo is never shown (B114): Grow does not keep it, so
   * the gardener would see a face nobody else does. With none, the letter
   * shows. A + badge on the circle, always, is the button that opens the picker.
   */
  const stored = mysqlUser?.profile_photo_url;
  /** Our upload in the public bucket (users/<id>/<file>), else none. */
  const photoUrl = useMemo(() => {
    if (stored && mysqlUser?.user_id && spaces) {
      return `${spaces}/users/${mysqlUser.user_id}/${stored}`;
    }
    return '';
  }, [stored, spaces, mysqlUser?.user_id]);

  /**
   * A picture chosen: shrunk, uploaded and shown at once; a failure is said
   * in a toast.
   *
   * It used to say "Request failed with status code 400" when the server had
   * written exactly what was wrong with the file. A file the browser cannot
   * read as a picture is said as that, plainly, before anything is sent;
   * an iPhone photo that would not convert, as that.
   *
   * @param file - The picture chosen (camera, device or Google Photos).
   */
  const handleUpload = async (file: File): Promise<void> => {
    if (!mysqlUser || !currentUser) return;

    setIsUploading(true);
    try {
      const prepared = await preparePhoto(file);
      if (!prepared.ok) {
        // Whatever step failed, to the gardener it is a file that is not a photo they can use.
        debugLog('UserProfile', 'Could not process the profile photo:', prepared.message);
        toast({ title: t('sitewide.error'), description: prepared.message, variant: 'destructive' });
        return;
      }

      const formData = new FormData();
      formData.append('photo', prepared.file);

      const result = await uploadProfilePhoto(mysqlUser.user_id, formData);
      if (!result.success) throw new Error(t('profile.photo.error'));

      // The URL does not change when the picture does, so the browser would
      // keep showing the old one; the timestamp is what forces a fresh fetch.
      updateMysqlUser({
        ...result.user,
        profile_photo_url: `${result.user.profile_photo_url}?t=${Date.now()}`,
      });
    } catch (error) {
      debugLog('UserProfile', 'Error uploading profile photo:', error);
      toast({
        title: t('sitewide.error'),
        description: errorMessage(error, t('profile.photo.error')),
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  /** Removes the uploaded picture, once asked; the letter shows again. */
  const handleDelete = async (): Promise<void> => {
    if (!mysqlUser) return;
    setRemoving(true);
    try {
      await deleteProfilePhoto(mysqlUser.user_id);
      updateMysqlUser({ profile_photo_url: null });
      setConfirming(false);
    } catch (error) {
      debugLog('UserProfile', 'Error deleting profile photo:', error);
      toast({
        title: t('sitewide.error'),
        description: errorMessage(error, t('profile.photo.deleteFailed')),
        variant: 'destructive',
      });
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="relative h-20 w-20 shrink-0 rounded-full bg-muted">
        {photoUrl ? (
          <img
            // Keyed on the address, so a new picture after one that failed to
            // load is drawn afresh rather than staying hidden.
            key={photoUrl}
            src={photoUrl}
            alt={t('profile.photo.title')}
            className="h-full w-full rounded-full object-cover"
            onError={(e) => {
              debugLog('UserProfile', 'Error loading image:', e);
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <UserAvatar mysqlUser={mysqlUser} firebaseUser={currentUser} className="h-full w-full text-3xl" />
        )}
        {!isUploading && (
          <button
            type="button"
            onClick={() => setChoosingPhoto(true)}
            aria-label={photoUrl ? t('profile.photo.change') : t('profile.photo.upload')}
            className="absolute inset-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {/* The + is the cue; the whole circle is the button. */}
            <span aria-hidden="true" className="absolute bottom-0 right-0 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-background">
              <Plus className="h-4 w-4" />
            </span>
          </button>
        )}
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center rounded-full bg-background/60">
            <Spinner size={28} />
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => setChoosingPhoto(true)} loading={isUploading} busy="uploading" disabled={isUploading}>
          <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
          {photoUrl ? t('profile.photo.change') : t('profile.photo.upload')}
        </Button>
        {stored && (
          <Button
            variant="outline"
            onClick={() => setConfirming(true)}
            disabled={isUploading}
            className="text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('profile.photo.delete')}
          </Button>
        )}
      </div>
      <BottomPanel
        open={confirming}
        onOpenChange={(next) => { if (!next && !removing) setConfirming(false); }}
        title={t('profile.photo.removeTitle')}
      >
        <p className="text-sm text-muted-foreground">{t('profile.photo.removeBody')}</p>
        <ActionRow onCancel={() => setConfirming(false)} cancelLabel={t('profile.photo.removeKeep')} disabled={removing}>
          <Button variant="destructive" size="lg" loading={removing} busy="deleting" onClick={() => { void handleDelete(); }}>
            {t('profile.photo.delete')}
          </Button>
        </ActionRow>
      </BottomPanel>
      <PhotoDialogShell isOpen={choosingPhoto} onClose={() => setChoosingPhoto(false)} title={photoUrl ? t('profile.photo.change') : t('profile.photo.upload')}>
        <PhotoSourcePicker
          camera={camera}
          onCaptured={(file) => { setChoosingPhoto(false); void handleUpload(file); }}
          onFileChosen={(file) => { setChoosingPhoto(false); void handleUpload(file); }}
          onPickGoogle={google.pick}
          isPickingGoogle={google.isPicking}
          googleStatus={google.status}
          googleCount={google.count}
          onCancelGoogle={google.cancel}
          googleError={google.error}
        />
      </PhotoDialogShell>
    </div>
  );
};
